
// src/OfflineDownloadButton.jsx
import React, { useEffect, useState } from 'react';
import { registerServiceWorker, requestOfflineCache, checkOfflineCache } from './registerSW';

export default function OfflineDownloadButton({ assetsUrl = '/offline-assets.json', className = '', compact = false }) {
  const [status, setStatus] = useState('idle'); // idle | working | done | error
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    registerServiceWorker();

    // Check if assets are already cached on mount
    const checkCache = async () => {
      const result = await checkOfflineCache(assetsUrl);
      if (result.cached) {
        setStatus('done');
        setCount(result.count);
      }
      setChecking(false);
    };

    checkCache();
  }, [assetsUrl]);

  const handleClick = async () => {
    setStatus('working');
    setProgress({ current: 0, total: 0 });

    const res = await requestOfflineCache(assetsUrl, (prog) => {
      setProgress(prog);
    });

    if (res?.ok) {
      setCount(res.count || 0);
      setStatus('done');
    } else {
      setStatus('error');
    }
  };

  // Download icon SVG
  const DownloadIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );

  const CheckIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const SpinnerIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="10" opacity="0.25" />
      <path d="M12 2 A10 10 0 0 1 22 12" opacity="1" />
    </svg>
  );

  if (compact) {
    // Don't show button while checking cache
    if (checking) return null;

    return (
      <button
        onClick={handleClick}
        className={`w-11 h-11 rounded-full flex items-center justify-center
                   bg-white/20 backdrop-blur-md border border-white/30
                   hover:bg-white/30 active:scale-95 transition-all
                   ${status === 'done' ? 'bg-green-500/80 border-green-400' : ''}`}
        aria-label={
          status === 'idle' ? 'Download for offline use' :
          status === 'working' ? 'Downloading...' :
          status === 'done' ? `Offline ready (${count} files)` :
          'Download failed'
        }
        aria-busy={status === 'working'}
        disabled={status === 'working' || status === 'done'}
        title={
          status === 'idle' ? 'Download for offline use' :
          status === 'working' ? 'Downloading...' :
          status === 'done' ? `Offline ready (${count} files)` :
          'Download failed, try again'
        }
      >
        <span className="text-white">
          {status === 'idle' && <DownloadIcon />}
          {status === 'working' && <SpinnerIcon />}
          {status === 'done' && <CheckIcon />}
          {status === 'error' && <DownloadIcon />}
        </span>
      </button>
    );
  }

  const percentage = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  // Show loading while checking cache
  if (checking) {
    return (
      <div className="w-full">
        <button
          className={className || "w-full rounded-xl border px-4 py-3 font-medium"}
          disabled
        >
          Checking offline status...
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <button
        onClick={handleClick}
        className={className || "w-full rounded-xl border px-4 py-3 font-medium"}
        aria-busy={status === 'working'}
        disabled={status === 'working' || status === 'done'}
      >
        {status === 'idle' && "Download this tour for offline use"}
        {status === 'working' && `Downloading ${progress.current}/${progress.total} files (${percentage}%)`}
        {status === 'done' && `✓ Offline ready (${count} files cached)`}
        {status === 'error' && "Couldn't cache offline. Try again on Wi‑Fi."}
      </button>

      {/* Progress bar */}
      {status === 'working' && progress.total > 0 && (
        <div className="w-full mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  );
}
